/**
 * Visit schedule hooks (訪問入力 - Recepro A / Recepro R).
 * Massagers + week visits (list); schedule by month, detail, create/update/submit-total (create/edit/copy flow).
 */

import { useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { useClinicContext } from './useClinicContext';
import useAppToast from '@/hooks/useAppToast';
import { normalizeVisitErrorMessage } from '@/components/molecules/VisitErrorMessageList';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import {
  getVisitScheduleMassagers,
  getVisitScheduleDayDetail,
  getVisitScheduleVisits,
  checkTreatmentIsExist,
  checkVisitScheduleWeekHasData,
  getVisitDetail,
  getDistanceChangeData,
  saveDistanceChanges,
  type VisitScheduleDayDetail,
  type VisitScheduleDetailResponse,
  type VisitScheduleMassagersResponse,
  type VisitScheduleIndexResponse,
  type VisitScheduleMassager,
  type VisitScheduleVisit,
  type SubmitTotalPayload,
  type DistanceChangeData,
  type DistanceSavePayload,
} from '@/services/main/visitSchedule';
import type { VisitDraftPayload } from '@/pages/main/visitSchedule/types';

function getServiceTypeFallback(): string {
  return typeof localStorage !== 'undefined' ? localStorage.getItem('service_type') || SERVICE_TYPE_A2 : SERVICE_TYPE_A2;
}

// ---- Massagers (dropdown) ----
export function useVisitScheduleMassagers(visitdate?: string | null) {
  const { tenant, clinicNumber } = useClinicContext();
  const theme = useTheme();
  const serviceType =
    theme?.serviceType === SERVICE_TYPE_R2 || theme?.serviceType === SERVICE_TYPE_A2 ? theme.serviceType : getServiceTypeFallback();
  const toast = useAppToast();
  const { data, isLoading, isError, isPending, refetch, error } = useQuery<
    VisitScheduleMassagersResponse,
    Error
  >({
    queryKey: ['visit-schedule', 'massagers', tenant, clinicNumber, serviceType, visitdate ?? ''],
    queryFn: () => getVisitScheduleMassagers(tenant, clinicNumber, serviceType, visitdate ?? undefined),
    enabled: !!tenant && !!clinicNumber,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  const raw = data?.data;
  const nested = raw as unknown as { data?: VisitScheduleMassager[] } | undefined;
  const massagers: VisitScheduleMassager[] = Array.isArray(raw)
    ? raw
    : Array.isArray(nested?.data)
      ? nested!.data
      : [];
  return { data, massagers, isLoading, isError, isPending, refetch, error };
}

// ---- Week visits (list by massager_id + visitdate) ----
// HIT-4726: 「距離が離れすぎています。{N}km」 (previous-patient distance) must not be shown on
// the 訪問入力 screen — only 「施術所からの距離が離れすぎています。{N}km」 (from clinic) is kept.
// Strip it once here so the calendar cards and the detail modal (both derived from these visit
// objects) stay consistent. Display-only: the stored value and the DistanceErrorCascader (which
// reads the DB column directly) are untouched.
const HIDDEN_VISIT_ERROR_PREFIXES = ['距離が離れすぎています。'];

function stripHiddenVisitErrors(visit: VisitScheduleVisit): VisitScheduleVisit {
  const isHidden = (msg?: string | null): boolean =>
    !!msg && HIDDEN_VISIT_ERROR_PREFIXES.some((p) => msg.startsWith(p));

  let errorMessages = visit.error_messages;
  if (typeof errorMessages === 'string' && errorMessages) {
    const parts = errorMessages.split(/[,\n]/);
    const kept = parts
      .filter((s) => !isHidden(s.trim()))
      .map((s) => normalizeVisitErrorMessage(undefined, s));
    if (kept.length !== parts.length) {
      errorMessages = kept.join('\n');
    } else {
      errorMessages = kept.join('\n');
    }
  }

  const errorItems = Array.isArray(visit.error_items)
    ? visit.error_items
        .filter((i) => !isHidden(i?.message))
        .map((i) => ({
          ...i,
          message: normalizeVisitErrorMessage(i?.code, i?.message ?? ''),
        }))
    : visit.error_items;

  if (errorMessages === visit.error_messages && errorItems === visit.error_items) {
    return visit;
  }
  return { ...visit, error_messages: errorMessages, error_items: errorItems };
}

export function useVisitScheduleVisits(
  massagerId: number | null,
  visitdate: string | null,
  clinicLocationId: number | null = null,
) {
  const { tenant, clinicNumber } = useClinicContext();
  const theme = useTheme();
  const serviceType =
    theme?.serviceType === SERVICE_TYPE_R2 || theme?.serviceType === SERVICE_TYPE_A2
      ? theme.serviceType
      : getServiceTypeFallback();
  const toast = useAppToast();
  const enabled = !!tenant && !!clinicNumber && !!massagerId && !!visitdate;
  const { data, isLoading, isError, isPending, isFetching, refetch, error } = useQuery<
    VisitScheduleIndexResponse,
    Error
  >({
    // clinic_location_id is part of the cache key — switching locations on the
    // same massager must invalidate the cached visits list.
    queryKey: ['visit-schedule', 'visits', tenant, clinicNumber, serviceType, massagerId, visitdate, clinicLocationId],
    queryFn: () =>
      getVisitScheduleVisits(
        tenant,
        clinicNumber,
        {
          massager_id: massagerId!,
          visitdate: visitdate!,
          ...(clinicLocationId ? { clinic_location_id: clinicLocationId } : {}),
        },
        serviceType
      ),
    enabled,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  const visits = useMemo<VisitScheduleVisit[]>(() => {
    const raw = data?.data;
    const nested = raw as unknown as { data?: VisitScheduleVisit[] } | undefined;
    const list: VisitScheduleVisit[] = Array.isArray(raw)
      ? raw
      : Array.isArray(nested?.data)
        ? nested!.data
        : [];
    return list.map(stripHiddenVisitErrors);
  }, [data]);
  return { data, visits, isLoading, isError, isPending, isFetching, refetch, error };
}

export function useVisitScheduleWeekCheckMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const toast = useAppToast();

  const { mutateAsync: checkWeekMutation, isPending: isLoading } = useMutation<
    VisitScheduleIndexResponse,
    Error,
    { massagerId: number; visitdate: string; clinicLocationId?: number | null }
  >({
    mutationFn: ({ massagerId, visitdate, clinicLocationId }) =>
      getVisitScheduleVisits(
        tenant,
        clinicNumber,
        {
          massager_id: massagerId,
          visitdate,
          clinic_location_id: clinicLocationId ?? null,
        },
        serviceType
      ),
    onError: error => {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    },
  });

  return { checkWeekMutation, isLoading };
}

/**
 * Existence check for the copy-week guard. Returns whether ANY Home Visit
 * exists in next week for this massager + clinic_location. 来院 / Treatment
 * rows (previous_visit_id = -1) are excluded — Treatment is separate data
 * and must not block 次週にコピー.
 */
export function useVisitScheduleWeekHasDataMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const toast = useAppToast();

  const { mutateAsync: checkWeekHasDataMutation, isPending: isLoading } = useMutation<
    boolean,
    Error,
    { massagerId: number; visitdate: string; clinicLocationId?: number | null }
  >({
    mutationFn: ({ massagerId, visitdate, clinicLocationId }) =>
      checkVisitScheduleWeekHasData(
        tenant,
        clinicNumber,
        {
          massager_id: massagerId,
          visitdate,
          clinic_location_id: clinicLocationId ?? null,
        },
        serviceType
      ),
    onError: error => {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    },
  });

  return { checkWeekHasDataMutation, isLoading };
}

export function useVisitScheduleTreatmentCheckMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync: checkTreatmentMutation, isPending: isLoading } = useMutation<
    boolean,
    Error,
    { patientId: number; visitDates: string[] }
  >({
    mutationFn: ({ patientId, visitDates }) =>
      checkTreatmentIsExist(tenant, clinicNumber, patientId, visitDates),
    onError: error => {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    },
  });

  return { checkTreatmentMutation, isLoading };
}

export function useVisitScheduleDayDetail(
  massagerId: number | null,
  visitdate: string | null,
  clinicLocationId: number | null = null
) {
  const { tenant, clinicNumber } = useClinicContext();
  const theme = useTheme();
  const serviceType =
    theme?.serviceType === SERVICE_TYPE_R2 || theme?.serviceType === SERVICE_TYPE_A2
      ? theme.serviceType
      : getServiceTypeFallback();
  const toast = useAppToast();
  const enabled = !!tenant && !!clinicNumber && !!massagerId && !!visitdate;

  const { data, isLoading, isError, isPending, refetch, error } = useQuery<
    VisitScheduleDetailResponse,
    Error
  >({
    queryKey: ['visit-schedule', 'detail-day', tenant, clinicNumber, serviceType, massagerId, visitdate, clinicLocationId],
    queryFn: () =>
      getVisitScheduleDayDetail(
        tenant,
        clinicNumber,
        {
          massager_id: massagerId!,
          visitdate: visitdate!,
          ...(clinicLocationId ? { clinic_location_id: clinicLocationId } : {}),
        },
        serviceType
      ),
    enabled,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  const raw = data?.data;
  const nested = raw as unknown as { data?: VisitScheduleDayDetail } | undefined;
  const detail: VisitScheduleDayDetail | null =
    raw && !Array.isArray(raw) && 'rows' in raw
      ? (raw as VisitScheduleDayDetail)
      : nested?.data ?? null;

  return { data, detail, isLoading, isError, isPending, refetch, error };
}

export function useVisitScheduleCreateMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  const { mutateAsync: createMutation, isPending: isLoading } = useMutation({
    mutationFn: (payload: VisitDraftPayload) =>
      apiClient
        .post(clinicApiPath(tenant, clinicNumber, '/visits'), payload)
        .then(res => res.data),
    onSuccess: response => {
      if (response?.success) {
        queryClient.invalidateQueries({ queryKey: ['visits'] });
        queryClient.invalidateQueries({ queryKey: ['visit-schedule'] });
        toast.success(response.message ?? '下書きを保存しました。');
      } else {
        toast.error(response?.message ?? '保存に失敗しました。');
      }
    },
    onError: err => {
      toast.error(getApiErrorMessage(err, '保存に失敗しました。'));
    },
  });

  return { createMutation, isLoading };
}

export function useVisitScheduleUpdateMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  const { mutateAsync: updateMutation, isPending: isLoading } = useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: VisitDraftPayload }) =>
      apiClient
        .put(clinicApiPath(tenant, clinicNumber, `/visits/${id}`), payload)
        .then(res => res.data),
    onSuccess: response => {
      if (response?.success) {
        queryClient.invalidateQueries({ queryKey: ['visits'] });
        queryClient.invalidateQueries({ queryKey: ['visit-schedule'] });
        toast.success(response.message ?? '下書きを更新しました。');
      } else {
        toast.error(response?.message ?? '更新に失敗しました。');
      }
    },
    onError: err => {
      toast.error(getApiErrorMessage(err, '更新に失敗しました。'));
    },
  });

  return { updateMutation, isLoading };
}

export function useVisitScheduleSubmitTotalMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  const { mutateAsync: submitTotalMutation, isPending: isLoading } = useMutation({
    // Opt out of global 404 toast (データが見つかりません。) and invalidation —
    // onError below already shows the BE message via getApiErrorMessage, and
    // we want to preserve local UI state (selected visits, pending edits)
    // while the user retries.
    meta: { suppressNotFoundToast: true, suppressNotFoundInvalidate: true },
    mutationFn: (payload: SubmitTotalPayload) =>
      apiClient
        .post(clinicApiPath(tenant, clinicNumber, '/visits/submit-total'), payload, {
          headers: { 'X-Service-Type': serviceType },
        })
        .then(res => res.data),
    onSuccess: response => {
      if (response?.success) {
        queryClient.invalidateQueries({ queryKey: ['visits'] });
        queryClient.invalidateQueries({ queryKey: ['visit-schedule'] });
        toast.success(response?.data?.message ?? response?.message ?? '更新が正常に終了しました。');
      } else {
        toast.error(response?.message ?? '更新ができませんでした。');
      }
    },
    onError: err => {
      toast.error(getApiErrorMessage(err, '更新ができませんでした。'));
    },
  });

  return { submitTotalMutation, isLoading };
}

/**
 * On-demand existence check for an existing DB visit. Used by the 訪問入力
 * modal's 適用 button to abort if the visit was deleted elsewhere.
 *
 * Returns a mutation so the caller can `await` imperatively inside an event
 * handler. 404 bubbles as AxiosError; caller inspects `err.response.status`.
 */
export function useVisitScheduleExistsCheckMutation() {
  const { tenant, clinicNumber } = useClinicContext();

  const { mutateAsync: checkVisitExists, isPending: isLoading } = useMutation({
    // Caller handles the 404 UX itself (toast + abort). Opt out of global
    // MutationCache toast + invalidation so no duplicate feedback fires.
    meta: { suppressNotFoundToast: true, suppressNotFoundInvalidate: true },
    mutationFn: (id: string | number) => getVisitDetail(tenant, clinicNumber, id),
  });

  return { checkVisitExists, isLoading };
}

/** 距離編集: fetch distance change data for a day + massager */
export function useDistanceChangeData(
  massagerId: number | null,
  visitdate: string | null,
  clinicLocationId: number | null = null
) {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();

  const { data, isLoading, refetch } = useQuery({
    // serviceType is already passed to the API below — it must be in the key too. (HIT-11387)
    queryKey: ['distance-change', tenant, clinicNumber, massagerId, visitdate, clinicLocationId, serviceType],
    queryFn: () =>
      getDistanceChangeData(
        tenant,
        clinicNumber,
        {
          massager_id: massagerId!,
          visitdate: visitdate!,
          ...(clinicLocationId ? { clinic_location_id: clinicLocationId } : {}),
        },
        serviceType
      ),
    enabled: !!massagerId && !!visitdate,
  });

  return {
    data: (data?.data ?? null) as DistanceChangeData | null,
    isLoading,
    refetch,
  };
}

/** 距離編集: save distance changes */
export function useDistanceSaveMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const toast = useAppToast();

  const { mutateAsync: saveMutation, isPending: isLoading } = useMutation({
    mutationFn: (payload: DistanceSavePayload) =>
      saveDistanceChanges(tenant, clinicNumber, payload, serviceType),
    onSuccess: response => {
      if (response?.success) {
        toast.success(response?.message ?? '更新が正常に終了しました。');
      } else {
        toast.error(response?.message ?? '更新ができませんでした。');
      }
    },
    onError: err => {
      toast.error(getApiErrorMessage(err, '更新ができませんでした。'));
    },
  });

  return { saveMutation, isLoading };
}
